import { TagByIdQuery, useTagSplitMutation } from "gql/generated";
import { useCallback } from "react";
import { useFormHandler, usePostHog } from "shared/hooks";
import { createMultilingual, getMultilingualString } from "shared/utils";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { InternalRefetchQueriesInclude } from "@apollo/client";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Adminland]);

const tagSplitFlyupFormSchema = z.object({
  id: z.string().min(1),
  nameForCurrentTag: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(
        "adminland:PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS.TAG_SPLIT_MODAL.ERRORS.NAME_FOR_CURRENT_TAG",
      ),
    ),
  namesForNewTags: z.array(
    z
      .string()
      .trim()
      .min(
        1,
        i18n.t(
          "adminland:PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS.TAG_SPLIT_MODAL.ERRORS.NAMES_FOR_NEW_TAGS",
        ),
      ),
  ),
});

export type TagSplitFlyupFormValues = z.infer<typeof tagSplitFlyupFormSchema>;

type UseTagSplitFlyupFormProps = {
  tag: TagByIdQuery["tagById"];
  onSuccess: (
    updatedExistingTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => void;
  onError: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
};

export const useTagSplitFlyupForm = ({
  tag,
  onSuccess,
  onError,
  refetchQueries,
}: UseTagSplitFlyupFormProps) => {
  const defaultValues: TagSplitFlyupFormValues = {
    id: tag?._id ?? "",
    nameForCurrentTag: getMultilingualString(tag?.name ?? {}),
    namesForNewTags: [""],
  };

  const { trackPostHogException } = usePostHog();

  const [tagSplitMutation] = useTagSplitMutation();

  return useFormHandler<TagSplitFlyupFormValues>(
    useCallback(
      async data => {
        if (
          !data ||
          !data.id ||
          !data.nameForCurrentTag ||
          !data.namesForNewTags ||
          data.namesForNewTags.length === 0
        ) {
          return;
        }

        try {
          const result = await tagSplitMutation({
            variables: {
              tagSplitId: data.id,
              nameForCurrentTag: createMultilingual(data.nameForCurrentTag),
              namesForNewTags: data.namesForNewTags.map(name =>
                createMultilingual(name ?? ""),
              ),
            },
            ...(refetchQueries ? { refetchQueries } : {}),
          });
          if (result.errors) {
            throw new Error(result.errors?.[0]?.message);
          }
          if (result.data?.tagSplit) {
            onSuccess(result.data.tagSplit[0], result.data.tagSplit.slice(1));
          }
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useTagSplitFlyupForm.ts",
            method: "onSubmit",
            message: "Error splitting tag",
          });
          onError();
        }
      },
      [
        refetchQueries,
        onError,
        onSuccess,
        tagSplitMutation,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: tagSplitFlyupFormSchema, mode: "onChange" },
  );
};
